import { readAccountConfig } from './config.ts';
import type { AccountClient, AuthMethods } from './types.ts';

/**
 * Which account service this page uses, and when it is loaded. There are three answers:
 *
 * - **supabase**: the build has a project's URL and key. The library is loaded only when it is
 *   needed: at once if the page comes back from a provider or an e-mail link, or if a session is
 *   saved on this device; otherwise when an account screen asks (`load`).
 * - **demo**: a development build (or `VITE_ENABLE_DEBUG`) was asked for the stand-in backend with
 *   `?account=demo` (remembered for the tab) or `VITE_ACCOUNT_MODE=demo`. It is a test double and
 *   the account screens say so.
 * - **none**: neither. The game is complete without an account; account screens say it is not
 *   available in this version.
 */

export type AccountMode = 'supabase' | 'demo' | 'none';

export interface AccountSetup {
  mode: AccountMode;
  /** Whether there is an account service at all; false hides the account entry points. */
  available: boolean;
  /** True when the service has to start with the page (a returning session or a callback). */
  eager: boolean;
  /** The Turnstile site key, when the service asks for a captcha. */
  turnstileSiteKey: string | null;
  /** The ways of getting in that work (besides the guest one); all of them when not said. */
  methods?: AuthMethods;
  load: () => Promise<AccountClient>;
}

export interface PlanInput {
  env: ImportMetaEnv;
  /** `location.search`, `location.pathname` and the browser's two storages (any may be missing). */
  search: string;
  pathname: string;
  origin: string;
  local: Pick<Storage, 'getItem' | 'key' | 'length'> | null;
  session: Pick<Storage, 'getItem' | 'setItem'> | null;
}

const DEMO_FLAG = 'ilcolpo.account';
const DEMO_STATE_KEY = 'ilcolpo.demo.auth';
const CALLBACK_PATH = '/auth/callback';

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

/** True when this device holds a saved Supabase session (the library's own storage key). */
function hasSavedSession(local: PlanInput['local']): boolean {
  if (!local) return false;
  return safe(() => {
    for (let i = 0; i < local.length; i++) {
      const key = local.key(i);
      if (key && /^sb-.+-auth-token$/.test(key)) return true;
    }
    return false;
  }, false);
}

export function planAccount(input: PlanInput): AccountSetup {
  const { env } = input;
  const debug = env.DEV || env.VITE_ENABLE_DEBUG === 'true';
  // Development builds can choose the service: `?account=demo` (the stand-in) or `?account=none`
  // (no account at all, whatever keys the build has: the e2e tests of the default experience use it).
  // The choice is remembered for the tab; `VITE_ACCOUNT_MODE=demo` makes it for the whole build.
  const remembered = safe(() => input.session?.getItem(DEMO_FLAG) ?? null, null);
  const asked =
    [
      new URLSearchParams(input.search).get('account'),
      remembered,
      env.VITE_ACCOUNT_MODE ?? null,
    ].find((c) => c === 'demo' || c === 'none') ?? null;
  if (debug && asked) {
    safe(() => {
      input.session?.setItem(DEMO_FLAG, asked);
    }, undefined);
  }

  if (debug && asked === 'demo') {
    return {
      mode: 'demo',
      available: true,
      eager:
        input.pathname === CALLBACK_PATH ||
        safe(() => (input.local?.getItem(DEMO_STATE_KEY) ?? null) !== null, false),
      turnstileSiteKey: null,
      load: async () => {
        // A separate chunk that only development builds have: in a production build this test is
        // false when the bundle is made, and the import (with the database in it) is not part of it.
        if (import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG === 'true') {
          const { createDemoAccount } = await import('./dev/demoAccount.ts');
          return createDemoAccount(`${input.origin}${CALLBACK_PATH}`);
        }
        throw new Error('the stand-in account backend is not part of this build');
      },
    };
  }

  const config = debug && asked === 'none' ? null : readAccountConfig(env, input.origin);
  if (config) {
    return {
      mode: 'supabase',
      available: true,
      eager: input.pathname === CALLBACK_PATH || hasSavedSession(input.local),
      turnstileSiteKey: config.turnstileSiteKey,
      methods: config.methods,
      load: async () => {
        const { createSupabaseAccount } = await import('./supabaseClient.ts');
        return createSupabaseAccount(config);
      },
    };
  }

  return {
    mode: 'none',
    available: false,
    eager: false,
    turnstileSiteKey: null,
    load: async () => {
      const { createNoAccount } = await import('./noAccount.ts');
      return createNoAccount();
    },
  };
}

/** The plan for this page. */
export function planForThisPage(): AccountSetup {
  return planAccount({
    env: import.meta.env,
    search: location.search,
    pathname: location.pathname,
    origin: location.origin,
    local: safe(() => localStorage, null),
    session: safe(() => sessionStorage, null),
  });
}
